import { enableProdMode, importProvidersFrom, ErrorHandler } from '@angular/core';

// Recarga automática cuando un chunk JS lazy no se encuentra tras un nuevo deploy
class ChunkErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    if (/Loading chunk [\d]+ failed/.test(error?.message ?? '')) {
      window.location.reload();
      return;
    }
    console.error(error);
  }
}
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthInterceptor } from './app/pages/interceptors/auth.interceptor';

// ✅ PrimeNG imports (nuevo sistema de configuración)
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

// ============================================================
// 🆕 INICIO: F2RT Custom Theme Preset - 2025-01-05
// ============================================================
// Preset personalizado basado en Aura con azul como color primario
// Autor: Claude Code
const F2RTPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#e6f2ff',   // Lightest tint
      100: '#b3d7ff',
      200: '#80bcff',
      300: '#4da1ff',
      400: '#1a86ff',
      500: '#007bff',  // Base: Azul ACC / Bootstrap primary
      600: '#006ce0',
      700: '#005cbf',
      800: '#004c9e',
      900: '#003d7d',
      950: '#002d5c'   // Darkest shade
    }
  }
});
// ============================================================
// 🆕 FIN: F2RT Custom Theme Preset
// ============================================================

// ✅ Módulos de terceros
import { ToastrModule } from 'ngx-toastr';
import { OAuthModule } from 'angular-oauth2-oidc';
import {
  NgxUiLoaderModule,
  NgxUiLoaderRouterModule,
  NgxUiLoaderHttpModule,
  NgxUiLoaderConfig,
  POSITION,
  SPINNER,
  PB_DIRECTION
} from 'ngx-ui-loader';

// ✅ App principal
import { AppComponent } from './app/app.component';
import { AppRoutingModule } from './app/app.routing.module';
import { SharedModule } from './app/components/shared/shared.module';
import { environment } from './environments/environment';

// ⚙️ Configuración del loader global
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: '#007bff', // 🔵 Azul primario
  bgsOpacity: 0.9,
  bgsPosition: POSITION.bottomRight, // ✅ esquina inferior derecha
  bgsSize: 60,
  bgsType: SPINNER.cubeGrid,
  fgsType: SPINNER.cubeGrid,
  fgsPosition: POSITION.bottomRight, // ✅ también para el spinner principal
  pbColor: '#007bff', // 🔵 Azul primario
  hasProgressBar: false, // 🔥 oculta la barra superior
  blur: 0,
  overlayColor: 'rgba(255,255,255,0)',
  text: '', // sin texto
  textColor: '#007bff' // 🔵 Azul primario
};

// ⚙️ Modo producción
if (environment.production) {
  enableProdMode();
}

// 🚀 Bootstrap principal
bootstrapApplication(AppComponent, {
  providers: [
    provideAnimationsAsync(),
    providePrimeNG({
      zIndex: {
        modal: 1100,    // Default - el dialog mask/container
        overlay: 1200,  // Dropdowns, selects - SOBRE el dialog
        menu: 1200,     // Menus popup - SOBRE el dialog
        tooltip: 1300,  // Tooltips siempre arriba de todo
      },
      theme: {
        preset: F2RTPreset, // 🌈 Tema F2RT con azul como primario
        options: {
          darkModeSelector: false, // 🔒 Deshabilitar detección automática de modo oscuro (fix fondos negros)
        },
      },
    }),
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    provideHttpClient(withInterceptorsFromDi()),

    // Auth Interceptor: maneja 401→logout, 403→notificación (Fase 5 auditoría seguridad)
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

    // Timezone por defecto para pipes | date en templates HTML (Colombia UTC-5)
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: '-0500' } },

    importProvidersFrom(
      AppRoutingModule,
      SharedModule,
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
      }),
      OAuthModule.forRoot(),
      // 🟢 Configuración final de loaders
      NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
      NgxUiLoaderRouterModule,
      NgxUiLoaderHttpModule.forRoot({ showForeground: false })
    ),
  ],
}).catch(err => console.error(err));
